import { filterNavigationItems, NAVIGATION_CONFIG } from '~/config/navigation';
import { REPORTS } from '~/config/reportsConfig';
import { getSettingsRoutePath, settingsRouteGroups } from '~/config/settingsRouteMetadata';
import {
  isHoaMenuItemHidden,
  isHoaOnlyMenuItem,
  isHoaSettingsChildHidden,
  isHoaSettingsGroupHidden,
  IS_HOA_DEMO_MODE,
} from '~/utils/hoaDemoMode';
import type { NavigationItem } from '~/config/navigation';
import type { MagicDoorFeature } from '~/swagger/portal/Api';

export type NavigationDestinationSource = 'menu' | 'settings' | 'report';

export type NavigationDestination = {
  id: string;
  labelKey: string;
  groupKey?: string;
  path: string;
  source: NavigationDestinationSource;
  keywords: string[];
  feature?: MagicDoorFeature;
  menuAccessKey?: string;
  /** Hidden from search unless the signed-in manager holds this permission. */
  permission?: { resource: string; action: string };
};

const REPORT_GROUP_KEY = 'Reports';

const MENU_KEYWORDS: Record<string, string[]> = {
  Payables: ['bill', 'bills', 'pay bill', 'pay bills', 'vendor bill', 'accounts payable'],
  'Bank accounts': ['banking'],
  'Chart of accounts': ['coa', 'gl accounts'],
  'Manual entries': ['journal entry', 'journal entries'],
  'Deposit slips': ['deposits'],
  'Owner funds': ['owner distribution', 'owner distributions', 'payout'],
  Leases: ['lease'],
  'Rental applications': ['applicant', 'applications'],
  'Add offline payments': ['record payment', 'offline payment', 'cash payment'],
  'Lease renewal': ['renew lease'],
  Tenants: ['residents'],
  Owners: ['landlords'],
  'Property managers': ['staff', 'employees', 'users'],
  Properties: ['portfolio', 'portfolios', 'buildings', 'units'],
  'Work orders': ['work order', 'wo', 'workorder'],
  'Maintenance requests': ['maintenance request', 'repair', 'service request'],
  Vendors: ['contractor', 'contractors'],
  Chats: ['messages', 'messaging', 'sms', 'text'],
  Announcements: ['broadcast', 'notice'],
  Listing: ['listings', 'vacancy', 'advertise'],
  Violations: ['violation'],
  Reports: ['report', 'reporting'],
  Settings: ['setting', 'configuration', 'preferences'],
};

const SETTINGS_KEYWORDS: Record<string, string[]> = {
  'property/late-fees': ['late fee'],
  'property/rent-payment': ['rent payment method'],
  'property/tenant-payment-reminders': ['payment reminder', 'reminders'],
  'company/payment-accounts': ['invoice settings'],
  'portal/invite-url': ['portal url', 'invite link'],
  'portal/invite-text': ['email template', 'text template', 'invite email'],
  'chats/email': ['email settings'],
  'chats/text': ['sms settings'],
  'lease/renewal': ['renewal settings'],
  'rental/applicationfee': ['application fee'],
};

const collectMenuDestinations = (
  items: NavigationItem[],
  parentKey: string | undefined,
  parentMenuAccessKey: string | undefined,
  result: NavigationDestination[]
) => {
  for (const item of items) {
    if (isHoaOnlyMenuItem(item.id) ? !IS_HOA_DEMO_MODE : isHoaMenuItemHidden(item.id)) continue;

    const menuAccessKey = parentMenuAccessKey ?? item.id.toLowerCase();

    if (item.canNavigate) {
      result.push({
        id: `menu:${item.path}`,
        labelKey: item.id,
        groupKey: parentKey,
        path: item.realPath ?? item.path,
        source: 'menu',
        keywords: MENU_KEYWORDS[item.id] ?? [],
        feature: item.requiredFeature,
        menuAccessKey,
      });
    }

    if (item.children) {
      collectMenuDestinations(item.children, item.id, menuAccessKey, result);
    }
  }
};

const buildSettingsDestinations = (): NavigationDestination[] =>
  settingsRouteGroups
    .filter((group) => !isHoaSettingsGroupHidden(group.labelKey))
    .flatMap((group) =>
      group.children
        .filter((child) => !child.hiddenFromOverview)
        .filter((child) => !isHoaSettingsChildHidden(child.path))
        .map((child) => ({
          id: `settings:${child.path}`,
          labelKey: child.labelKey,
          groupKey: group.labelKey,
          path: getSettingsRoutePath(child.path),
          source: 'settings' as const,
          keywords: SETTINGS_KEYWORDS[child.path] ?? [],
          feature: child.feature,
          permission: child.permission,
          menuAccessKey: 'settings',
        }))
    );

const buildReportDestinations = (): NavigationDestination[] =>
  REPORTS.flatMap((report) => {
    const reportDestination: NavigationDestination = {
      id: `report:${report.path}`,
      labelKey: report.labelKey,
      groupKey: REPORT_GROUP_KEY,
      path: report.path,
      source: 'report',
      keywords: report.searchKeywords ?? [],
      feature: report.feature,
      menuAccessKey: 'reports',
    };

    const nestedDestinations = report.actions
      .filter((action) => action.path && action.path !== report.path)
      .map((action) => ({
        id: `report:${action.path}`,
        labelKey: action.labelKey,
        groupKey: REPORT_GROUP_KEY,
        path: action.path as string,
        source: 'report' as const,
        keywords: [],
        feature: report.feature,
        menuAccessKey: 'reports',
      }));

    return [reportDestination, ...nestedDestinations];
  });

export const buildNavigationDestinations = (checkFeature: (feature: MagicDoorFeature) => boolean): NavigationDestination[] => {
  const menuDestinations: NavigationDestination[] = [];
  collectMenuDestinations(filterNavigationItems(NAVIGATION_CONFIG, checkFeature), undefined, undefined, menuDestinations);

  return [...menuDestinations, ...buildReportDestinations(), ...buildSettingsDestinations()].filter(
    (destination) => !destination.feature || checkFeature(destination.feature)
  );
};
